import { Controller } from "@hotwired/stimulus";
import { showBlock, hide, isMobileDevice } from "../../utils";
import { setOptions, importLibrary } from "@googlemaps/js-api-loader";

export default class extends Controller {
  static values = {
    apiKey: String,
    mapId: String,
    existingAddresses: Array,
  };

  static targets = [
    "autocompleteContainer",
    "street1Field",
    "street2Field",
    "cityField",
    "stateField",
    "zipField",
    "addressFieldsContainer",
  ];

  connect() {
    // Prevent mobile zoom on this page
    if (isMobileDevice()) {
      this.preventMobileZoom();
    }

    this.initializeGoogleMaps();
  }

  preventMobileZoom() {
    const viewport = document.querySelector('meta[name="viewport"]');
    if (viewport) {
      viewport.setAttribute("content", "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no");
    } else {
      const newViewport = document.createElement("meta");
      newViewport.name = "viewport";
      newViewport.content = "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no";
      document.head.appendChild(newViewport);
    }
  }

  scrollToTopOnMobile() {
    // Check if we're on a mobile device using the utility method
    if (isMobileDevice()) {
      // Smooth scroll to the top of the page
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }

  async initializeGoogleMaps() {
    try {
      await this.loadGoogleMapsAPI();
      this.setupPlaceAutocomplete();
    } catch (error) {
      console.error("Error initializing Google Maps:", error);
      this.showAddressFields();
      this.hideGoogleMapsAddressAutocomplete();
    }
  }

  async loadGoogleMapsAPI() {
    if (window.google?.maps) {
      return;
    }

    setOptions({
      key: this.apiKeyValue,
      v: "weekly",
      libraries: ["places"],
    });

    await importLibrary("places");
  }

  setupPlaceAutocomplete() {
    try {
      // Create the PlaceAutocompleteElement with options
      const placeAutocomplete = new google.maps.places.PlaceAutocompleteElement({
        componentRestrictions: { country: ["us", "ca"] }, // Restrict to US and Canada
      });

      // Style the autocomplete element
      placeAutocomplete.style.width = "100%";
      placeAutocomplete.id = "place-autocomplete-input";

      // Force light mode styling by setting CSS properties directly
      placeAutocomplete.style.setProperty("color-scheme", "light");

      // Clear the container and add the autocomplete element
      this.autocompleteContainerTarget.innerHTML = "";
      this.autocompleteContainerTarget.appendChild(placeAutocomplete);

      // Add event listener for when user starts typing (mobile scroll to top)
      placeAutocomplete.addEventListener("focus", () => {
        this.scrollToTopOnMobile();
      });

      // Also listen for input events to handle cases where user clicks and starts typing
      placeAutocomplete.addEventListener("input", (event) => {
        // Only scroll on the first character typed
        if (event.target?.value?.length === 1) {
          this.scrollToTopOnMobile();
        }
      });

      placeAutocomplete.addEventListener("gmp-select", async (event) => {
        try {
          if (event.placePrediction) {
            const place = event.placePrediction.toPlace();
            this.handlePlaceSelection(place);
          }
        } catch (error) {
          console.error("Error processing place selection:", error);
        }
      });

      // Hide the original address fields since we're replacing them
      this.hideAddressFields();
    } catch (error) {
      console.error("Error setting up place autocomplete:", error);
      // Show original fields if autocomplete fails
      this.showAddressFields();
    }
  }

  populateAddressFields(place) {
    // Initialize all fields to empty
    const addressComponents = {
      street_number: "",
      route: "",
      locality: "",
      administrative_area_level_1: "",
      postal_code: "",
      subpremise: "",
    };

    // Parse address components - check both new API format and legacy format
    const components = place.addressComponents || place.address_components;

    if (components) {
      components.forEach((component) => {
        // Handle both new API format (component.types) and legacy format (component.types)
        const types = component.types;
        const longName = component.longText || component.long_name;
        const shortName = component.shortText || component.short_name;

        if (types.includes("street_number")) {
          addressComponents.street_number = longName;
        } else if (types.includes("route")) {
          addressComponents.route = longName;
        } else if (types.includes("locality")) {
          addressComponents.locality = longName;
        } else if (types.includes("administrative_area_level_1")) {
          addressComponents.administrative_area_level_1 = shortName;
        } else if (types.includes("postal_code")) {
          addressComponents.postal_code = longName;
        } else if (types.includes("subpremise")) {
          addressComponents.subpremise = longName;
        }
      });
    }

    // Populate the form fields with proper fallbacks
    const street1 = `${addressComponents.street_number} ${addressComponents.route}`.trim() || "";
    const street2 = addressComponents.subpremise || "";

    if (this.hasStreet1FieldTarget) this.street1FieldTarget.value = street1;
    if (this.hasStreet2FieldTarget) this.street2FieldTarget.value = street2;
    if (this.hasCityFieldTarget) this.cityFieldTarget.value = addressComponents.locality || "";
    if (this.hasZipFieldTarget) this.zipFieldTarget.value = addressComponents.postal_code || "";
    if (this.hasStateFieldTarget) this.stateFieldTarget.value = addressComponents.administrative_area_level_1 || "";

    this.showAddressFields();

    // Trigger duplicate address validation after populating fields
    this.validateDuplicateAddress();
  }

  hideAddressFields() {
    // Hide the original address input fields but keep them for form submission
    if (this.hasAddressFieldsContainerTarget) {
      hide(this.addressFieldsContainerTarget);
    }
  }

  hideGoogleMapsAddressAutocomplete() {
    const container = this.element.querySelector("#google-maps-address-autocomplete");
    if (container) {
      hide(container);
    }
  }

  showAddressFields() {
    // Show the address fields after autocomplete selection for user review/editing
    if (this.hasAddressFieldsContainerTarget) {
      showBlock(this.addressFieldsContainerTarget);
    }
  }

  async handlePlaceSelection(place) {
    try {
      // Fetch the required fields if it's a place object
      if (place && typeof place.fetchFields === "function") {
        await place.fetchFields({
          fields: ["addressComponents", "formattedAddress", "location"],
        });
      }

      this.populateAddressFields(place);
    } catch (error) {
      console.error("Error handling place selection:", error);
    }
  }

  validateDuplicateAddress() {
    // Trigger validation by dispatching an input event on the street1 field
    // This will cause the applicant form controller to run its standard validation
    if (this.hasStreet1FieldTarget) {
      const event = new Event("input", { bubbles: true });
      this.street1FieldTarget.dispatchEvent(event);
    }
  }

  disconnect() {
    // Clean up mutation observer if it exists
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
  }
}
